import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LuMail, LuMessageCircle, LuBookOpen } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import { TERMS_URL, PRIVACY_URL } from '../../config/marketingSite.js';
import {
  SUPPORT_EMAIL, SUPPORT_MAILTO, SUPPORT_CHAT_MAILTO, FAQ_ROUTE,
} from '../../config/support.js';
import { isNative } from '../../platform/platform.js';
import { openExternal } from '../../platform/navigation.js';
import './SupportPage.less';

// Email and chat both open the OS mail composer for the ONE configured
// support address (src/config/support.js); chat pre-fills a subject so
// support can tell them apart. Help centre is the in-app FAQ route.
const CONTACTS = [
  { key: 'email', href: SUPPORT_MAILTO, icon: LuMail, title: 'Email support', desc: SUPPORT_EMAIL },
  { key: 'chat', href: SUPPORT_CHAT_MAILTO, icon: LuMessageCircle, title: 'Chat with us', desc: 'Send us a message by email.' },
  { key: 'help', to: FAQ_ROUTE, icon: LuBookOpen, title: 'Help centre', desc: 'Answers to the most common questions.' },
];

function SupportPage() {
  // On web the anchor's default click already does the right thing —
  // `target="_blank"` + `noopener` protection. On native we intercept
  // so the Capacitor Browser plugin renders an in-app browser view
  // instead of bouncing out to Safari / Chrome. mailto: / tel: are
  // handed off to the OS handler by `openExternal`.
  const handleClick = useCallback((href) => (e) => {
    if (!isNative()) return; // web: honour the anchor default
    e.preventDefault();
    openExternal(href).catch(() => {});
  }, []);

  return (
    <Page>
      <PageHeader
        title="Support"
        subtitle="We usually reply within one business day."
      />

      <div className="gc-support-list">
        {CONTACTS.map(({ key, href, to, icon: Icon, title, desc }) => {
          const body = (
            <>
              <span className="gc-support-icon" aria-hidden="true"><Icon /></span>
              <div className="gc-support-text">
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </>
          );
          // In-app route: plain router navigation on web and native.
          if (to) {
            return (
              <Card key={key} as={Link} to={to} interactive className="gc-support-row">
                {body}
              </Card>
            );
          }
          return (
            <Card
              key={key}
              as="a"
              href={href}
              interactive
              className="gc-support-row"
              target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
              onClick={handleClick(href)}
            >
              {body}
            </Card>
          );
        })}
      </div>

      <Card>
        <h3 className="gc-support-legal-title">Legal</h3>
        <ul className="gc-support-legal-list">
          <li><a href={TERMS_URL} target="_blank" rel="noopener noreferrer" onClick={handleClick(TERMS_URL)}>Terms of Service</a></li>
          <li><a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" onClick={handleClick(PRIVACY_URL)}>Privacy Policy</a></li>
        </ul>
      </Card>
    </Page>
  );
}

export default SupportPage;
